// src/utils/declarationNcs.ts — SCE Issues
// Déclaration de réclamation fournisseur (NC-S) préparée depuis un problème
// (05/10 ; ouverte à tous les internes le 06/10, fournisseurs exclus).
//
// ─── Le circuit remplacé ───
//
// Jusqu'ici : remplir à la main le formulaire Excel « Déclarations
// réclamation fournisseur (NC-S) » (M2-SOP-013-FRM-01) et l'envoyer par mail
// à la qualité. Désormais l'appli remplit ce formulaire et le DÉPOSE, un
// fichier par déclaration (« NC-S <Composant> <NVS> JJMMAA »), dans un dossier
// SharePoint ; le flow 5 l'envoie à la qualité.
//
// ─── Pourquoi un fichier par déclaration, et pas un onglet ───
//
// Essayé d'abord (05/10) : un onglet par déclaration dans un classeur unique.
// Il fallait remplacer le classeur ENTIER, et SharePoint refuse dès qu'il est
// ouvert dans Excel (423, puis 409) — ce qui est son état normal à la
// qualité. Un fichier neuf, lui, n'est jamais verrouillé ni écrasé. Et
// l'onglet « CopyNon-conformity » de chaque fichier, qui lit
// « NCDeclaration », continue de servir au report dans le registre.
//
// ─── Le modèle ───
//
// Le classeur vierge sur SharePoint : son onglet « NCDeclaration » est rempli
// SUR PLACE par chirurgie XML (même approche que l'export « Montage et
// Tests ») — logo, listes déroulantes, protection, liens : tout le reste est
// recopié octet pour octet. Les cellules de saisie sont toutes réécrites,
// vides comprises : une saisie oubliée dans le modèle ne passe pas dans la
// déclaration.
//
// Les valeurs écrites sont celles des listes déroulantes du formulaire, à
// l'identique (« Late/ Unavailable », espace compris) : la qualité les
// reprend telles quelles dans son registre.
//
// Pur : aucun React, aucun réseau.

import { A_QUALIFIER, basculerBadge, type BadgeCloture, type Equipement, type Issue, type Phase } from '@/types/issue.types'
import { chargerJSZip, echapperXml, poserCellule, type ValeurCellule } from '@/utils/xlsxCellules'
import { desechapper, feuillesDuClasseur, lire, textesPartages } from '@/utils/xlsxLecture'

// ─── Les listes du formulaire (feuille « Drop-down list ») ───

/**
 * Lieu de détection. Les lieux « C-… » (chez le client) sont écartés : ils
 * ouvrent le bloc « informations client » du formulaire, hors sujet pour une
 * réclamation fournisseur.
 */
export const LIEUX_DETECTION = [
  'S-Supplier',
  'S-Logistic Reception',
  'S-Incoming QC',
  'I-Design',
  'I-Production',
  'I-Test',
  'I-FAT',
  'I-Final QC',
] as const

/** Lieux en français, pour le formulaire de l'appli. */
export const LIBELLES_LIEUX: Readonly<Record<string, string>> = {
  'S-Supplier': 'Chez le fournisseur',
  'S-Logistic Reception': 'Réception logistique',
  'S-Incoming QC': 'Contrôle réception',
  'I-Design': 'Conception',
  'I-Production': 'Production',
  'I-Test': 'Test',
  'I-FAT': 'FAT',
  'I-Final QC': 'Contrôle final',
}

/** Catégorie de problème, valeurs exactes de la liste (traductions : bulle d'aide du formulaire). */
export const CATEGORIES_PROBLEME = [
  'Identification/Traceability issue',
  'Late/ Unavailable',
  'Product Function',
  'Component Function',
  'Software Function',
  'Packaging/Shipment',
  'Customer Specification',
  'Documentation',
  'QMS not respected',
  'Service',
  'Surface finish/ Breakage',
  'Dimensional Defect',
  'Non-compliant specification/instruction',
  'Security',
] as const

export const LIBELLES_CATEGORIES: Readonly<Record<string, string>> = {
  'Identification/Traceability issue': 'Identification, traçabilité',
  'Late/ Unavailable': 'Retard, indisponible',
  'Product Function': 'Fonction produit',
  'Component Function': 'Fonction composant',
  'Software Function': 'Fonction logiciel',
  'Packaging/Shipment': 'Emballage, expédition',
  'Customer Specification': 'Spécification client',
  'Documentation': 'Documentation',
  'QMS not respected': 'SMQ non respecté',
  'Service': 'Service',
  'Surface finish/ Breakage': 'Finition de surface, casse',
  'Dimensional Defect': 'Défaut dimensionnel',
  'Non-compliant specification/instruction': 'Spécification ou instruction non conforme',
  'Security': 'Sécurité',
}

export type PrioriteNcs = 1 | 2 | 3

/** Priorité, selon la bulle d'aide du formulaire ; `court` pour le sélecteur. */
export const PRIORITES: readonly { valeur: PrioriteNcs; court: string; libelle: string }[] = [
  { valeur: 1, court: 'Bloquée', libelle: 'Production bloquée' },
  { valeur: 2, court: 'Dégradée', libelle: 'Production en mode dégradé' },
  { valeur: 3, court: 'Sans impact', libelle: "Pas d'impact sur la production" },
]

// ─── Ce qu'on déclare ───

export interface DeclarationNcs {
  /** B7 — date d'enregistrement et d'envoi à la qualité : le jour même. */
  dateNotification: Date
  /** B9 */
  lieuDetection: string
  /** B10 — format de la liste « People list » : « Nom Prénom ». */
  initiateur: string
  /** B11 — survenue réelle du problème. */
  dateOccurrence: Date
  /** B14 */
  priorite: PrioriteNcs | null
  /** B15 */
  explicationPriorite: string
  /** B18 — désignation du composant, telle que sur son étiquette. */
  designationComposant: string
  /** E18 */
  nvs: string
  /** B19 */
  categorie: string
  /** E19 — N° de série de la PIÈCE fautive, pas de l'équipement (qui est dans la description). */
  numeroSerie: string
  /** B20 */
  quantite: number | null
  /** B23 */
  specification: string
  /** B25 */
  description: string
}

/** Ce que le champ NVS contient d'office : il ne reste qu'à taper les chiffres. */
export const PREFIXE_NVS = 'NVS'

/** Le NVS saisi, « NVS » laissé seul comptant pour rien. */
export function nvsSaisi(nvs: string): string {
  const t = nvs.trim()
  return t.toUpperCase() === PREFIXE_NVS ? '' : t
}

/** Ce qui manque pour déclarer, dans l'ordre du formulaire. Vide = prêt. */
export function champsManquants(d: DeclarationNcs): string[] {
  const manque: string[] = []
  if (!d.lieuDetection) manque.push('le lieu de détection')
  if (!d.initiateur.trim()) manque.push("l'initiateur")
  if (d.priorite === null) manque.push('la priorité')
  if (!d.designationComposant.trim()) manque.push('la désignation du composant')
  if (!d.categorie) manque.push('la catégorie de problème')
  if (d.quantite === null || !(d.quantite > 0)) manque.push('la quantité défectueuse')
  if (!d.description.trim()) manque.push('la description')
  return manque
}

/** La phase du problème donne le lieu de détection le plus probable. */
const LIEU_PAR_PHASE: Record<Phase, string> = {
  Consultation: 'I-Design',
  'Ingénierie': 'I-Design',
  Fabrication: 'I-Production',
  Test: 'I-Test',
  'Expédition': 'I-Final QC',
}

/** Criticité de l'appli → priorité du formulaire (proposée, modifiable). */
function prioriteDepuis(issue: Issue): PrioriteNcs | null {
  switch (issue.criticite) {
    case 'Critique': return 1
    case 'Majeur': return 2
    case 'Mineur': return 3
    case A_QUALIFIER: return null
    default: {
      const jamais: never = issue.criticite
      return jamais
    }
  }
}

/** Mots d'un nom, sans casse ni espaces parasites : « Vautelin  Sébastien » = « Sébastien Vautelin ». */
function cleNom(nom: string): string {
  return nom.trim().toLowerCase().split(/\s+/).filter(Boolean).sort().join(' ')
}

/**
 * L'initiateur tel qu'il est écrit dans la liste du formulaire (« Delavault
 * Guillaume », espaces compris), sinon « Nom Prénom » reconstitué.
 * @param nom « Prénom Nom » (nomComplet).
 */
export function initiateurDepuis(nom: string, personnes: readonly string[]): string {
  const cle = cleNom(nom)
  if (!cle) return ''
  const trouve = personnes.find((p) => cleNom(p) === cle)
  if (trouve) return trouve
  const mots = nom.trim().split(/\s+/)
  return mots.length > 1 ? `${mots.slice(1).join(' ')} ${mots[0]}` : nom.trim()
}

/** Jour local d'une date ISO, à minuit. */
function jourDe(iso: string, repli: Date): Date {
  const d = new Date(iso)
  const base = Number.isNaN(d.getTime()) ? repli : d
  return new Date(base.getFullYear(), base.getMonth(), base.getDate())
}

/**
 * Déclaration pré-remplie depuis le problème. Ce que l'appli ne sait pas —
 * le composant fautif (désignation, NVS, N° de série de la pièce), sa
 * catégorie de défaut — reste à saisir. L'équipement va dans la description.
 * @param lien adresse de la fiche dans l'appli (photos et historique).
 */
export function preremplirNcs(
  issue: Issue,
  equipement: Equipement | null | undefined,
  initiateur: string,
  lien: string,
  aujourdhui: Date,
): DeclarationNcs {
  const specification = [
    ...(issue.numeroPlan ?? '').split('\n').map((p) => p.trim()).filter(Boolean).map((p) => `Plan ${p}`),
    ...(issue.numeroEDE
      ? [`Schéma électrique ${issue.numeroEDE}${issue.revEDE ? ` Rev ${issue.revEDE}` : ''}${issue.pageEDE ? `, page ${issue.pageEDE}` : ''}`]
      : []),
  ].join('\n')

  return {
    dateNotification: new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate()),
    lieuDetection: LIEU_PAR_PHASE[issue.phase] ?? '',
    initiateur,
    dateOccurrence: jourDe(issue.dateDeclaration, aujourdhui),
    priorite: prioriteDepuis(issue),
    explicationPriorite: '',
    designationComposant: '',
    // Le NVS vient en premier dans le formulaire, déjà commencé (06/10).
    nvs: PREFIXE_NVS,
    categorie: '',
    numeroSerie: '',
    quantite: 1,
    specification,
    description: descriptionDepuis(issue, equipement, lien),
  }
}

/** Titre, description, puis l'équipement et le renvoi vers la fiche de l'appli. */
function descriptionDepuis(issue: Issue, equipement: Equipement | null | undefined, lien: string): string {
  const titre = issue.titre.trim()
  const texte = issue.description.trim()
  const machine = [
    issue.numeroSerie,
    equipement?.familleEquipement,
    issue.of,
    issue.pn,
  ].filter(Boolean).join(' · ')
  return [
    texte && texte !== titre ? `${titre}\n${texte}` : titre,
    machine ? `Équipement : ${machine}` : '',
    `SCE Issues ${issue.id}${lien ? ` (photos et historique) : ${lien}` : ''}`,
  ].filter(Boolean).join('\n\n')
}

// ─── Le fichier déposé ───

/** Caractères refusés par SharePoint dans un nom de fichier (et « # », « % » par prudence). */
const INTERDITS_FICHIER = /["*:<>?/\\|#%\u0000-\u001F]/g

/**
 * `NC-S Vanne papillon DN50 NVS12345 051026.xlsx` — règle de Guillaume
 * (05/10) : « NC-S <Composant> <NVS> JJMMAA », date de notification à la
 * qualité. Sans NVS, il est omis ; « (2) » si le nom est déjà pris.
 *
 * L'ID du problème n'est PAS dans le nom : il est porté par la colonne Titre
 * du fichier et ses propriétés de document (cf. `remplirFormulaireNcs`).
 */
export function nomFichierNcs(declaration: Pick<DeclarationNcs, 'designationComposant' | 'nvs' | 'dateNotification'>, existants: readonly string[]): string {
  const propre = (t: string): string => t.replace(INTERDITS_FICHIER, ' ').replace(/\s+/g, ' ').trim()
  const d = declaration.dateNotification
  const p = (n: number): string => String(n).padStart(2, '0')
  const base = [
    'NC-S',
    propre(declaration.designationComposant).slice(0, 80).replace(/[. ]+$/, ''),
    propre(nvsSaisi(declaration.nvs)).slice(0, 30),
    `${p(d.getDate())}${p(d.getMonth() + 1)}${p(d.getFullYear() % 100)}`,
  ].filter(Boolean).join(' ')
  const pris = new Set(existants.map((n) => n.toLowerCase()))
  if (!pris.has(`${base}.xlsx`.toLowerCase())) return `${base}.xlsx`
  for (let k = 2; ; k++) {
    const nom = `${base} (${k}).xlsx`
    if (!pris.has(nom.toLowerCase())) return nom
  }
}

// ─── Le badge et la trace dans l'activité ───

/**
 * Les badges du problème une fois la NC-S déclarée : NC-S ajouté (sans
 * doublon), « Autre » retiré — il veut dire « aucun circuit qualité », ce que
 * la déclaration dément. Même règle que `basculerBadge`.
 */
export function badgesAvecNcs(courants: readonly BadgeCloture[]): BadgeCloture[] {
  return courants.includes('NC-S') ? [...courants] : basculerBadge(courants, 'NC-S')
}

/** Ce que l'activité affiche (retour de Guillaume du 06/10) ; le fichier s'ouvre par une icône. */
export const LIBELLE_EVENEMENT_NCS = 'NC-S envoyée à la qualité'

const DEBUT_EVENEMENT = `${LIBELLE_EVENEMENT_NCS} : `
/** Format du 05/10, encore relu pour les événements déjà écrits. */
const DEBUT_EVENEMENT_V1 = 'Déclaration NC-S déposée pour la qualité : '

/**
 * Texte de l'événement « Modification » : le libellé, le fichier, son lien,
 * puis la priorité choisie (06/10 : le coordinateur la retrouve, pré-cochée,
 * en criticité). Le texte brut reste lisible tel quel (flow 4, SharePoint) ;
 * l'appli n'en montre que le libellé et une icône vers le fichier.
 */
export function texteEvenementNcs(fichier: string, lien: string, priorite?: PrioriteNcs | null): string {
  const p = PRIORITES.find((x) => x.valeur === priorite)
  return `${DEBUT_EVENEMENT}${fichier}\n${lien}${p ? `\nPriorité ${p.valeur} · ${p.libelle}` : ''}`
}

export interface EvenementNcs {
  fichier: string
  lien: string
  /** Absente des événements d'avant le 06/10. */
  priorite?: PrioriteNcs
}

/** Relit un événement de déclaration NC-S ; null pour tout autre événement. */
export function lireEvenementNcs(contenu: string): EvenementNcs | null {
  const debut = [DEBUT_EVENEMENT, DEBUT_EVENEMENT_V1].find((d) => contenu.startsWith(d))
  if (!debut) return null
  const [fichier = '', lien = '', ligneP = ''] = contenu.slice(debut.length).split('\n')
  if (!fichier.trim()) return null
  const p = /^Priorité ([123])\b/.exec(ligneP.trim())
  return { fichier: fichier.trim(), lien: lien.trim(), ...(p ? { priorite: Number(p[1]) as PrioriteNcs } : {}) }
}

/** Priorité de la NC-S → criticité proposée au coordinateur (1 bloquée → Critique…). */
export const CRITICITE_PAR_PRIORITE: Readonly<Record<PrioriteNcs, 'Critique' | 'Majeur' | 'Mineur'>> = {
  1: 'Critique',
  2: 'Majeur',
  3: 'Mineur',
}

// ─── Lecture et écriture du classeur ───

/** Onglet du formulaire, rempli à chaque déclaration. */
export const FEUILLE_MODELE = 'NCDeclaration'
/** Liste des personnes (masquée) : colonne A = « Nom Prénom ». */
const FEUILLE_PERSONNES = 'People list'

export interface ContenuClasseurNcs {
  /** Noms des onglets, dans l'ordre du classeur. */
  onglets: string[]
  /** Valeurs de la liste « Initiateur NC », telles qu'écrites. */
  personnes: string[]
}

/** Onglets et liste des personnes du classeur. */
export async function lireClasseurNcs(octets: ArrayBuffer | Uint8Array): Promise<ContenuClasseurNcs> {
  const zip = await (await chargerJSZip()).loadAsync(octets)
  const feuilles = await feuillesDuClasseur(zip)
  const personnes: string[] = []
  const liste = feuilles.find((f) => f.nom.trim() === FEUILLE_PERSONNES)
  if (liste?.chemin && zip.file(liste.chemin)) {
    const xml = await lire(zip, liste.chemin)
    const partages = await textesPartages(zip)
    for (const m of xml.matchAll(/<c r="A(\d+)"([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      if (Number(m[1]) < 2) continue // en-tête
      const type = /\st="(\w+)"/.exec(m[2] ?? '')?.[1]
      const corps = m[3] ?? ''
      const v = /<v[^>]*>([^<]*)<\/v>/.exec(corps)?.[1]
      const texte =
        type === 's' ? partages[Number(v)] :
        type === 'inlineStr' ? [...corps.matchAll(/<t[^>]*>([^<]*)<\/t>/g)].map((t) => desechapper(t[1] ?? '')).join('') :
        v !== undefined ? desechapper(v) : undefined
      if (texte?.trim()) personnes.push(texte)
    }
  }
  return { onglets: feuilles.map((f) => f.nom), personnes }
}

/** Cellules du formulaire « NCDeclaration ». */
export function cellulesDeclaration(d: DeclarationNcs): Record<string, ValeurCellule | undefined> {
  return {
    B7: d.dateNotification,
    B8: 'Complaint_supplier',
    B9: d.lieuDetection,
    B10: d.initiateur,
    B11: d.dateOccurrence,
    B14: d.priorite ?? undefined,
    B15: d.explicationPriorite.trim(),
    B18: d.designationComposant.trim(),
    E18: nvsSaisi(d.nvs),
    B19: d.categorie,
    E19: d.numeroSerie.trim(),
    B20: d.quantite ?? undefined,
    B23: d.specification.trim(),
    B25: d.description.trim(),
  }
}

/**
 * Le formulaire rempli : l'onglet « NCDeclaration » du modèle, cellules de
 * saisie TOUTES réécrites. Le reste du classeur n'est pas touché, sauf ses
 * propriétés de document : Titre = ID du problème (le nom du fichier ne le
 * porte plus ; SharePoint le reprend dans sa colonne Titre, que lit le
 * flow 5), Objet = « Déclaration NC-S ».
 * @throws Error si le modèle a changé de forme (onglet ou cellule introuvable).
 */
export async function remplirFormulaireNcs(
  modele: ArrayBuffer | Uint8Array,
  declaration: DeclarationNcs,
  issueId: string,
): Promise<ArrayBuffer> {
  const zip = await (await chargerJSZip()).loadAsync(modele)
  const formulaire = (await feuillesDuClasseur(zip)).find((f) => f.nom === FEUILLE_MODELE)
  if (!formulaire?.chemin) throw new Error(`Classeur NC-S modifié : onglet « ${FEUILLE_MODELE} » introuvable`)

  let feuille = await lire(zip, formulaire.chemin)
  for (const [ref, v] of Object.entries(cellulesDeclaration(declaration))) {
    feuille = poserCellule(feuille, ref, v, { vider: true })
  }
  zip.file(formulaire.chemin, feuille)

  // Tout recalculer à l'ouverture : sinon l'onglet « CopyNon-conformity » de
  // la qualité (des RECHERCHEV sur ce formulaire) garde les valeurs du modèle
  // vierge — des 0 — jusqu'au premier recalcul forcé. (Le modèle porte un
  // <calcPr> ; en ajouter un ailleurs rendrait le fichier illisible.)
  const classeur = await lire(zip, 'xl/workbook.xml')
  zip.file('xl/workbook.xml', classeur.replace(/<calcPr\s(?![^>]*fullCalcOnLoad)/, '<calcPr fullCalcOnLoad="1" '))

  const proprietes = zip.file('docProps/core.xml')
  if (proprietes) {
    const xml = poserPropriete(poserPropriete(await proprietes.async('string'), 'dc:title', issueId), 'dc:subject', 'Déclaration NC-S')
    zip.file('docProps/core.xml', xml)
  }
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' })
}

/** `<dc:title>…</dc:title>` (ou `<dc:title/>`) remplacé ; ajouté en tête s'il manque. */
function poserPropriete(xml: string, balise: string, valeur: string): string {
  const element = `<${balise}>${echapperXml(valeur)}</${balise}>`
  const motif = new RegExp(String.raw`<${balise}\s*/>|<${balise}>[\s\S]*?</${balise}>`)
  if (motif.test(xml)) return xml.replace(motif, () => element)
  return xml.replace(/(<cp:coreProperties\b[^>]*>)/, (debut: string) => `${debut}${element}`)
}
